import { apiFetch } from "../lib/api";

export interface BarOut {
  ts: string;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
}

export interface BarsResponse {
  bars: BarOut[];
  original_count: number;
  downsampled: boolean;
  bucket_size: number;
}

export interface BarsQuery {
  symbol: string;
  exchange: string;
  timeframe: string;
  start: string; // ISO
  end: string;
  instrument_type?: string;
  expiry?: string | null;
  strike?: string | null;
  lot_size?: number;
  tick_size?: string;
}

export async function getBars(q: BarsQuery): Promise<BarsResponse> {
  const params = new URLSearchParams({
    symbol: q.symbol,
    exchange: q.exchange,
    timeframe: q.timeframe,
    start: q.start,
    end: q.end,
  });
  if (q.instrument_type) params.set("instrument_type", q.instrument_type);
  if (q.expiry) params.set("expiry", q.expiry);
  if (q.strike) params.set("strike", q.strike);
  if (q.lot_size != null) params.set("lot_size", String(q.lot_size));
  if (q.tick_size) params.set("tick_size", q.tick_size);
  return apiFetch<BarsResponse>(`/bars?${params.toString()}`);
}

export interface TradedBaseSegment {
  symbol: string;
  expiry: string | null;
  start_ts: string;
  end_ts: string;
  bar_count: number;
}

export interface TradedBaseResponse {
  bars: BarOut[];
  segments: TradedBaseSegment[];
  base_instrument: string;
  root: string;
  original_count: number;
  downsampled: boolean;
  bucket_size: number;
}

export interface TradedBaseQuery {
  symbol: string;
  timeframe: string;
  start: string; // ISO
  end: string;
  base_instrument?: "futures" | "index";
  underlying?: string | null;
}

/** The stitched base series an option-routed backtest actually ran on —
 *  per-day near-month future (auto-rolled monthly) or the cash index. */
export async function getTradedBase(
  q: TradedBaseQuery,
): Promise<TradedBaseResponse> {
  const params = new URLSearchParams({
    symbol: q.symbol,
    timeframe: q.timeframe,
    start: q.start,
    end: q.end,
  });
  if (q.base_instrument) params.set("base_instrument", q.base_instrument);
  if (q.underlying) params.set("underlying", q.underlying);
  return apiFetch<TradedBaseResponse>(`/bars/traded-base?${params.toString()}`);
}

export interface LiveTailResponse {
  bars: BarOut[];
  live: boolean;
  source: string | null;
  detail: string | null;
}

export interface LiveTailQuery {
  symbol: string;
  exchange: string;
  timeframe: string;
  instrument_type?: string;
  expiry?: string | null;
  strike?: string | null;
  lot_size?: number;
  tick_size?: string;
}

export async function getLiveTail(q: LiveTailQuery): Promise<LiveTailResponse> {
  const params = new URLSearchParams({
    symbol: q.symbol,
    exchange: q.exchange,
    timeframe: q.timeframe,
  });
  if (q.instrument_type) params.set("instrument_type", q.instrument_type);
  if (q.expiry) params.set("expiry", q.expiry);
  if (q.strike) params.set("strike", q.strike);
  if (q.lot_size != null) params.set("lot_size", String(q.lot_size));
  if (q.tick_size) params.set("tick_size", q.tick_size);
  return apiFetch<LiveTailResponse>(`/bars/live-tail?${params.toString()}`);
}

export interface VolumeProfileLevelOut {
  lo: string;
  hi: string;
  volume: string;
}

export interface VolumeProfileResponse {
  poc: string;
  vah: string;
  val: string;
  total_volume: string;
  levels: VolumeProfileLevelOut[];
  used_timeframe: string;
  bar_count: number;
}

export interface VolumeProfileQuery {
  symbol: string;
  exchange: string;
  instrument_type?: string;
  expiry?: string | null;
  strike?: string | null;
  lot_size?: number;
  tick_size?: string;
  start: string;
  end: string;
  num_levels?: number;
  value_area_pct?: number;
  preferred_timeframe?: string;
  /** weekly/monthly/quarterly/daily — only honoured by the
   *  weekly-volume-profiles batch endpoint. */
  period?: "daily" | "weekly" | "monthly" | "quarterly";
  /** Bucket size multiplier: period=daily + period_count=2 → 2-day buckets.
   *  Epoch-anchored server-side, so boundaries are scroll-stable. */
  period_count?: number;
  /** When true, each bucket carries its developing-POC step series. */
  developing?: boolean;
}

export interface DevPocPoint {
  ts: string;
  poc: string;
}

export interface WeeklyVolumeProfileEntry {
  iso_year: number;
  iso_week: number;
  start_ts: string;
  end_ts: string;
  poc: string;
  vah: string;
  val: string;
  total_volume: string;
  levels: VolumeProfileLevelOut[];
  bar_count: number;
  developing?: DevPocPoint[] | null;
}

export interface WeeklyVolumeProfilesResponse {
  weeks: WeeklyVolumeProfileEntry[];
  used_timeframe: string;
}

export async function getWeeklyVolumeProfiles(
  q: VolumeProfileQuery,
): Promise<WeeklyVolumeProfilesResponse> {
  const params = new URLSearchParams({
    symbol: q.symbol,
    exchange: q.exchange,
    start: q.start,
    end: q.end,
  });
  if (q.instrument_type) params.set("instrument_type", q.instrument_type);
  if (q.expiry) params.set("expiry", q.expiry);
  if (q.strike) params.set("strike", q.strike);
  if (q.lot_size != null) params.set("lot_size", String(q.lot_size));
  if (q.tick_size) params.set("tick_size", q.tick_size);
  if (q.num_levels != null) params.set("num_levels", String(q.num_levels));
  if (q.value_area_pct != null)
    params.set("value_area_pct", String(q.value_area_pct));
  if (q.period) params.set("period", q.period);
  if (q.period_count != null) params.set("period_count", String(q.period_count));
  if (q.developing) params.set("developing", "true");
  return apiFetch<WeeklyVolumeProfilesResponse>(
    `/bars/weekly-volume-profiles?${params.toString()}`,
  );
}

export async function getVolumeProfile(
  q: VolumeProfileQuery,
): Promise<VolumeProfileResponse> {
  const params = new URLSearchParams({
    symbol: q.symbol,
    exchange: q.exchange,
    start: q.start,
    end: q.end,
  });
  if (q.instrument_type) params.set("instrument_type", q.instrument_type);
  if (q.expiry) params.set("expiry", q.expiry);
  if (q.strike) params.set("strike", q.strike);
  if (q.lot_size != null) params.set("lot_size", String(q.lot_size));
  if (q.tick_size) params.set("tick_size", q.tick_size);
  if (q.num_levels != null) params.set("num_levels", String(q.num_levels));
  if (q.value_area_pct != null)
    params.set("value_area_pct", String(q.value_area_pct));
  if (q.preferred_timeframe)
    params.set("preferred_timeframe", q.preferred_timeframe);
  return apiFetch<VolumeProfileResponse>(
    `/bars/volume-profile?${params.toString()}`,
  );
}
